/**
 * "Later" (Slack's Saved items + reminders, docs/design/slack-gap.md): each person's list of saved messages and
 * reminders, in three tabs — In progress, Archived, Completed. Saving a message from its menu adds it; "Remind me about
 * this" saves it with a reminder (`remindAt`); `/remind me <what> <when>` adds a reminder that has only text. When a
 * reminder is due the server adds a `reminder` Activity item, a notification and a push (it stays In progress, marked
 * overdue, until it is completed, snoozed or removed). Private to the person; nothing is broadcast to others.
 */
import { z } from "zod";
import { Id, Message, Timestamp } from "./entities.ts";

export const LaterState = z.enum(["in_progress", "archived", "completed"]);
export type LaterState = z.infer<typeof LaterState>;

/**
 * A recurring reminder (Slack "/remind me … every weekday at 9am"): local `minutes` after midnight in the zone the
 * person had when setting it (`offsetMinutes`, minutes to add to UTC). `every`: day, weekday (Mon–Fri), week (on
 * `weekday`, 0 = Sunday), month (on `monthDay`, the month's last day when it is shorter).
 */
export const ReminderRepeat = z.object({
  every: z.enum(["day", "weekday", "week", "month"]),
  weekday: z.number().int().min(0).max(6).optional(),
  monthDay: z.number().int().min(1).max(31).optional(),
  minutes: z.number().int().min(0).max(24 * 60 - 1),
  offsetMinutes: z.number().int().min(-14 * 60).max(14 * 60),
});
export type ReminderRepeat = z.infer<typeof ReminderRepeat>;

export const LaterItem = z.object({
  id: Id,
  state: LaterState,
  /** The saved message (absent for a `/remind` reminder made of text only). */
  messageId: Id.optional(),
  channelId: Id.optional(),
  /** The message as it is now (absent when it was deleted or I can no longer read it). */
  message: Message.optional(),
  /** A text reminder's "what" (`/remind me to call Ana at 3pm` → "call Ana"). */
  text: z.string().max(1000).optional(),
  /** When to remind me (absent = no reminder). */
  remindAt: Timestamp.optional(),
  /** When the current reminder fired (set once `remindAt` passed; cleared when it is moved). */
  remindedAt: Timestamp.optional(),
  /** Repeats: after it fires, `remindAt` moves to the next occurrence. */
  repeat: ReminderRepeat.optional(),
  /** Someone else set this reminder for me (`/remind @me …`). */
  fromUserId: Id.optional(),
  /**
   * A reminder I set for a conversation (`/remind #channel …`): at `remindAt` it is posted there ("Reminder: …")
   * instead of reminding me; a one-off one is then completed.
   */
  forChannelId: Id.optional(),
  savedAt: Timestamp,
  /** When it was completed (Completed tab order). */
  completedAt: Timestamp.optional(),
  updatedAt: Timestamp,
});
export type LaterItem = z.infer<typeof LaterItem>;

/** GET /v1/w/:ws/later — all my items (every tab), most recently saved first. */
export const ListLaterResponse = z.object({ items: z.array(LaterItem) });
export type ListLaterResponse = z.infer<typeof ListLaterResponse>;

/**
 * POST /v1/w/:ws/later — save a message (`messageId`) or add a text reminder (`text` + `remindAt`). Saving a message
 * already in my list returns that item (back to In progress, with the new `remindAt` when one is given).
 */
export const SaveLaterRequest = z.object({
  messageId: Id.optional(),
  text: z.string().min(1).max(1000).optional(),
  remindAt: Timestamp.optional(),
  /** A text reminder that repeats (`remindAt` is its first time). */
  repeat: ReminderRepeat.optional(),
  /** A text reminder for someone else (an active person in the workspace): it goes to their Later. */
  forUserId: Id.optional(),
  /** A text reminder posted into a conversation I can post in. */
  forChannelId: Id.optional(),
});
export type SaveLaterRequest = z.infer<typeof SaveLaterRequest>;

/**
 * PATCH /v1/w/:ws/later/:itemId — move between tabs and/or set (`remindAt`), snooze or clear (`null`) the reminder;
 * `stopRepeat` stops a recurring reminder (clearing the reminder stops it too).
 */
export const UpdateLaterRequest = z.object({
  state: LaterState.optional(),
  remindAt: Timestamp.nullable().optional(),
  stopRepeat: z.boolean().optional(),
});
export type UpdateLaterRequest = z.infer<typeof UpdateLaterRequest>;

export const LaterItemResponse = z.object({ item: LaterItem });
export type LaterItemResponse = z.infer<typeof LaterItemResponse>;
